import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, Chip, LinearProgress, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Warning as WarningIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';

type LocationState = {
  analysisText?: string;
  taskId?: string | null;
  dataKey?: string | null;
};

function extractSection(text: string, header: string): string[] {
  const re = new RegExp(`\\*\\*${header}\\:\\*\\*[\\\s\\S]*?(?=\\n\\n\\*\\*|$)`, 'i');
  const match = text.match(re);
  if (!match) return [];
  const section = match[0]
    .replace(new RegExp(`\\*\\*${header}\\:\\*\\*`, 'i'), '')
    .trim();
  return section
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('-'))
    .map((l) => l.replace(/^[-]\s*/, ''));
}

function extractScenario(text: string, scenario: 'Optimistic' | 'Realistic' | 'Accelerated'): string[] {
  // Match lines like "- **Optimistic:** ..." within Predictive Scenarios section
  const sectionRe = new RegExp(`\\*\\*Predictive Scenarios \(Optimistic \/ Realistic \/ Accelerated\)\\:\\*\\*[\\\s\\S]*?(?=\\n\\n\\*\\*|$)`, 'i');
  const sectionMatch = text.match(sectionRe);
  if (!sectionMatch) return [];
  const re = new RegExp(`-\\s*\\*\\*${scenario}\\:\\*\\*\\s*([^\n]+)`, 'i');
  const lines: string[] = [];
  const scenarioMatch = sectionMatch[0].match(re);
  if (scenarioMatch && scenarioMatch[1]) {
    lines.push(scenarioMatch[1].trim());
  }
  return lines;
}

function extractTradeOff(text: string, key: 'Time' | 'Cost'): string[] {
  const sectionRe = new RegExp(`\\*\\*Time vs\. Cost Trade-off Summary\\:\\*\\*[\\\s\\S]*?(?=\\n\\n\\*\\*|$)`, 'i');
  const sectionMatch = text.match(sectionRe);
  if (!sectionMatch) return [];
  const re = new RegExp(`-\\s*\\*\\*${key}\\:\\*\\*\\s*([^\n]+)`, 'i');
  const m = sectionMatch[0].match(re);
  return m && m[1] ? [m[1].trim()] : [];
}

function extractRecommendations(text: string): string[] {
  const re = new RegExp(`\\*\\*Recommended Project-level Optimisation Strategy\\:\\*\\*[\\\s\\S]*?(?=\\n\\n\\*\\*|$)`, 'i');
  const match = text.match(re);
  if (!match) return [];
  const section = match[0]
    .replace(/\\*\\*Recommended Project-level Optimisation Strategy\\:\\*\\*/i, '')
    .trim();
  return section
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => /^\d+\./.test(l))
    .map((l) => l.replace(/^\d+\.\s*/, ''));
}

function scoreRisk(text: string) {
  const lower = text.toLowerCase();
  const timeSignals = ['delay', 'overdue', 'lag', 'bottleneck', 'not commenced'];
  const costSignals = ['cost', 'overtime', 'investment', 'increase'];
  const timeCount = timeSignals.reduce((acc, s) => acc + (lower.includes(s) ? 1 : 0), 0);
  const costCount = costSignals.reduce((acc, s) => acc + (lower.includes(s) ? 1 : 0), 0);
  const time = Math.min(100, 40 + timeCount * 15);
  const cost = Math.min(100, 35 + costCount * 15);
  const severity = time >= 75 || cost >= 75 ? 'High' : time >= 55 || cost >= 55 ? 'Moderate' : 'Low';
  return { time, cost, severity };
}

const AIDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = (location.state || {}) as LocationState;
  const text = state.analysisText || '';

  const { delays, bottlenecks, optimistic, realistic, accelerated, timeSummary, costSummary, recommendations, risk } = useMemo(() => {
    const delays = extractSection(text, 'Cross-WBS Delay Overview');
    const bottlenecks = extractSection(text, 'Global Bottlenecks');
    const optimistic = extractScenario(text, 'Optimistic');
    const realistic = extractScenario(text, 'Realistic');
    const accelerated = extractScenario(text, 'Accelerated');
    const timeSummary = extractTradeOff(text, 'Time');
    const costSummary = extractTradeOff(text, 'Cost');
    const recommendations = extractRecommendations(text);
    const risk = scoreRisk(text);
    return { delays, bottlenecks, optimistic, realistic, accelerated, timeSummary, costSummary, recommendations, risk };
  }, [text]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">AI Planning Insights</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {state.taskId ? <Chip label={`task_id: ${state.taskId}`} /> : null}
          {state.dataKey ? <Chip label={`data_key: ${state.dataKey}`} /> : null}
          <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
          <Button
            variant="outlined"
            onClick={() =>
              navigate('/project-planning/ai-visuals', {
                state: {
                  delaysCount: delays.length,
                  bottlenecksCount: bottlenecks.length,
                  risk,
                  // pass full analysis state for richer visuals dashboard
                  delays,
                  bottlenecks,
                  optimistic,
                  realistic,
                  accelerated,
                  timeSummary,
                  costSummary,
                  recommendations,
                  taskId: state.taskId || null,
                  dataKey: state.dataKey || null,
                },
              })
            }
          >Open Visuals</Button>
        </Box>
      </Box>

      {/* Risk Overview */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 1 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">Overall Risk</Typography>
                <Typography variant="body2" color="text.secondary">Severity: {risk.severity}</Typography>
              </Box>
              <WarningIcon color={risk.severity === 'High' ? 'error' : risk.severity === 'Moderate' ? 'warning' : 'success'} />
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Time Risk</Typography>
              <LinearProgress variant="determinate" value={risk.time} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
              <Typography variant="caption" color="text.secondary">Cost Risk</Typography>
              <LinearProgress variant="determinate" value={risk.cost} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Cross-WBS Delays</Typography>
              <TimelineIcon color="primary" />
            </Box>
            <Box sx={{ mt: 1 }}>
              {delays.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No delays identified in the summary.</Typography>
              ) : (
                delays.map((d, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.75 }}>• {d}</Typography>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Global Bottlenecks</Typography>
              <AssessmentIcon color="primary" />
            </Box>
            <Box sx={{ mt: 1 }}>
              {bottlenecks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No bottlenecks summarized.</Typography>
              ) : (
                bottlenecks.map((b, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.75 }}>• {b}</Typography>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Predictive Scenarios */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Predictive Scenarios</Typography>
            <TrendingUpIcon color="primary" />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2">Optimistic</Typography>
              {optimistic.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No optimistic scenario described.</Typography>
              ) : optimistic.map((o, i) => (
                <Typography key={i} variant="body2">• {o}</Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="subtitle2">Realistic</Typography>
              {realistic.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No realistic scenario described.</Typography>
              ) : realistic.map((r, i) => (
                <Typography key={i} variant="body2">• {r}</Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="subtitle2">Accelerated</Typography>
              {accelerated.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No accelerated scenario described.</Typography>
              ) : accelerated.map((a, i) => (
                <Typography key={i} variant="body2">• {a}</Typography>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Time vs Cost */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6">Time vs. Cost Trade-off</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="subtitle2">Time</Typography>
              {timeSummary.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No time trade-off summary.</Typography>
              ) : timeSummary.map((t, i) => (
                <Typography key={i} variant="body2">• {t}</Typography>
              ))}
            </Box>
            <Box>
              <Typography variant="subtitle2">Cost</Typography>
              {costSummary.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No cost trade-off summary.</Typography>
              ) : costSummary.map((c, i) => (
                <Typography key={i} variant="body2">• {c}</Typography>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <Card>
        <CardContent>
          <Typography variant="h6">Recommended Actions</Typography>
          <Box sx={{ mt: 1 }}>
            {recommendations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No recommendations provided.</Typography>
            ) : recommendations.map((rec, i) => (
              <Typography key={i} variant="body2">{i + 1}. {rec}</Typography>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Raw content fallback */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Raw Analysis</Typography>
        <Card>
          <CardContent>
            {text ? (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">No analysis text available. Return to dashboard and re-check status.</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AIDetails;